// runtimeSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
// import { build } from 'vite';

const getSessionId = () => {
  let sessionId = sessionStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
};

const initialState = {
  API_URL: process.env.NODE_ENV === "development" ? "http://localhost:4000" : "https://api.estate-searcher.com",
  facetsLoaded: false,
  facets: {
    cities: {}, //deprecated use location
    districts: {}, //deprecated use location 
    types: {},
    agents: {},
    bedrooms: {},
    rentSale: {},
    locations: {}, //district: {location:count} nested facet object
  },
  sortBy: "new", //need to deprecate this and move to filters -> this is not an object and thus doesnt ensure referential integrity
  properties: {
    totalHits: 0,
    hits: [],
  }, // We'll store search results here
  total: 0, //deprecated -> now in properties
  filters: {
    district: [],
    location: [],//deprecated use city
    city: [],
    homeType: [],
    buyOrRent: [],
    price: [0, 999999999],
    bedrooms: 0,
    agents: [],
    houseLocation: [], //deprecated -> now in location
  },
  users: [],
  paginationOptions: {
    currentPage: 1,
    totalPages: 1,
  },
  capthcaVerified: false,
  navigation: {
    currentView: "home",
  },
  ads: {
    homepage_vertical: [],
    homepage_horizontal: [],
    search_page_horizontal: [],
    search_page_vertical: [],
    mobile: [],
  },
  session: {
    user: {},
    id: getSessionId(),
  },
};

const fetchProperties = createAsyncThunk(
  "runtime/fetchProperties",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const { filters, sortBy } = state.runtime;
    const { currentPage } = state.runtime.paginationOptions;

    // Build query params from filters
    const params = {
      state: filters.district,
      city: filters.city,
      location: filters.location,
      type: filters.homeType,
      buy_rent: Array.isArray(filters.buyOrRent) ? filters.buyOrRent[0] || "" : "",
      price_low: filters.price ? filters.price[0] : 0,
      price_high: filters.price ? filters.price[1] : 999999999,
      bedrooms: filters.bedrooms || 0,
      agent_id: filters.agents || [],
      sort: sortBy || "new",
      page: currentPage,
      session: state.runtime.session,
    };

    try {
      // Call /properties/search to get property data (no facet usage here)
      const response = await axios.get(`${state.runtime.API_URL}/properties/search`, { params });
      // Return only the property/pagination data you need
      return {
        facetDistribution: response.data.facetDistribution,
        hits: response.data.hits,
        pagination: response.data.pagination,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Failed to fetch properties."
      );
    }
  }
);


// New redux thunk for facets that will run only once when homepage gets rendered instead of every time.

const fetchSearchFacets = createAsyncThunk(
  "runtime/fetchSearchFacets",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const { filters, sortBy } = state.runtime;
    const { currentPage } = state.runtime.paginationOptions;

    // The same search params or a simplified one
    const params = {
      state: filters.district,
      city: filters.city,
      location: filters.location,
      type: filters.homeType,
      buy_rent: Array.isArray(filters.buyOrRent) ? filters.buyOrRent[0] || "" : "",
      price_low: filters.price ? filters.price[0] : 0,
      price_high: filters.price ? filters.price[1] : 999999999,
      bedrooms: filters.bedrooms || 0,
      sort: sortBy || "new",
      page: currentPage,
    };

    try {
      // Same endpoint /properties/search, but we only care about search (facets)
      const response = await axios.get(`${state.runtime.API_URL}/properties/search`, { params });

      // Return ONLY the facets from the API
      return {
        facets: response.data.facetDistribution || {},
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Failed to fetch facets."
      );
    }
  }
);

const getEstimatedHits = createAsyncThunk(
  'runtime/getEstimatedHits',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    try {
      const response = await axios.get(`${state.runtime.API_URL}/properties/estimate-hits`);
      console.log("Estimated hits:", response.data.estimatedTotalHits);
      return response.data.estimatedHits;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);


const fetchUsers = createAsyncThunk(
  'runtime/fetchUsers',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    try {
      const response = await axios.get(`${state.runtime.API_URL}/users`);
      return response.data.users;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);
const fetchAds = createAsyncThunk(
  'runtime/fetchAds',
  async (adType, thunkAPI) => {
    let url;
    const state = thunkAPI.getState();
    console.log("Fetching ads for", adType);
    switch (adType) {
      case 'homeVertical':
        url = `${state.runtime.API_URL}/ads/es-home/vertical`;
        console.log("trying ads on", url);
        break;
      case 'homeHorizontal':
        url = `${state.runtime.API_URL}/ads/es-home/horizontal`;
        console.log("trying ads on", url);
        break;
      case 'searchHorizontal':
        url = `${state.runtime.API_URL}/ads/es-search/horizontal`;
        console.log("trying ads on", url);
        break;
      case 'searchVertical':
        url = `${state.runtime.API_URL}/ads/es-search/vertical`;
        console.log("trying ads on", url);
        break;
      case 'mobile':
        url = `${state.runtime.API_URL}/ads/es-mobile`;
        console.log("trying ads on", url);
        break;
      default:
        return thunkAPI.rejectWithValue("Invalid ad type");
    }

    try {
      console.log("Trying to fetch ads from", url);
      const response = await axios.get(url);
      console.log(response.data);
      return { adType, ads: response.data.ads };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

const recordAdView = createAsyncThunk(
  'runtime/recordAdView',
  async (adId, thunkAPI) => {
    const state = thunkAPI.getState();
    const sessionId = state.runtime.session.id;

    try {
      const response = await axios.post(`${state.runtime.API_URL}/metrics/ads/view`, {
        adId,
        sessionId
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

const recordAdClick = createAsyncThunk(
  'runtime/recordAdClick',
  async (adId, thunkAPI) => {
    const state = thunkAPI.getState();
    const sessionId = state.runtime.session.id;

    try {
      const response = await axios.post(`${state.runtime.API_URL}/metrics/ads/record-click`, {
        adId,
        sessionId
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

const runtimeSlice = createSlice({
  name: 'runtime',
  initialState,
  reducers: {
    setSortBy(state, action) {
      console.log("Setting sort by", action.payload);
      state.sortBy = action.payload;
    },
    storeFilters(state, action) {
      console.log("storing filters", action.payload);
      state.filters = action.payload;
      localStorage.setItem("filters", JSON.stringify(action.payload));
    },
    setCurrentPage(state, action) {
      state.paginationOptions.currentPage = action.payload;
      console.log("Setting current page", action.payload);
    },
    setCaptchaVerified(state, action) {
      state.capthcaVerified = action.payload;
    },
    setCurrentView(state, action) {
      state.navigation.currentView = action.payload;
      console.log("Setting current view", action.payload);
    },
    clearFilters(state, action) {
      state.filters = initialState.filters;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProperties.fulfilled, (state, action) => {
      console.log("[REDUX THUNK] storing properties:", action.payload);
      state.properties.hits = action.payload.hits;
      state.properties.totalHits = action.payload.pagination.total;
      state.paginationOptions = {
        currentPage: action.payload.pagination.currentPage,
        totalPages: action.payload.pagination.totalPages,
      };
      state.facets.locations = action.payload.facetDistribution.location;
      state.facets.districts = Object.keys(action.payload.facetDistribution.location);
      // state.facets.districts = action.payload.facets.location
      // state.facets.cities = action.payload.facets.city;
      state.facets.types = action.payload.facetDistribution.type;
      state.facets.agents = action.payload.facetDistribution.agent_id;
      state.facets.bedrooms = action.payload.facetDistribution.bedrooms;
      state.facets.rentSale = action.payload.facetDistribution.rent_sale;
    });

    builder.addCase(fetchSearchFacets.fulfilled, (state, action) => {
      console.log("[REDUX] Storing facets in Redux:", action.payload); // Log to confirm response

      if (action.payload && action.payload.facets) {
        // Extract `facetDistribution` properly
        const dist = action.payload.facets;

        state.facets = {
          agents: dist.agent_id || {},
          cities: dist.city || {},
          districts: dist.state || {},  // "state" contains district-level data
          types: dist.type || {},
          rentSale: dist.rent_sale || {},
          bedrooms: dist.bedrooms || {},
        };
        state.facetsLoaded = true;
      }
      else {
        console.warn("API returned no facets");
      }

    });

    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      console.log("[REDUX THUNK] storing users:", action.payload);
      state.users = action.payload;
    });
    builder.addCase(getEstimatedHits.fulfilled, (state, action) => {
      console.log("[REDUX THUNK] storing estimated hits:", action.payload);
      state.properties.estimatedHits = action.payload;
    });
    builder.addCase(fetchAds.fulfilled, (state, action) => {
      console.log(`[REDUX THUNK] storing ${action.payload.adType} ads:`, action.payload.ads);
      switch (action.payload.adType) {
        case 'homeVertical':
          state.ads.homepage_vertical = action.payload.ads;
          break;
        case 'homeHorizontal':
          state.ads.homepage_horizontal = action.payload.ads;
          break;
        case 'searchHorizontal':
          state.ads.search_page_horizontal = action.payload.ads;
          break;
        case 'searchVertical':
          state.ads.search_page_vertical = action.payload.ads;
          break;
        case 'mobile':
          state.ads.mobile = action.payload.ads;
          break;
        default:
          break;
      }
    });
    builder.addCase(recordAdView.fulfilled, (state, action) => {
      console.log("[REDUX THUNK] Recorded ad view:", action.payload);
    });
    builder.addCase(recordAdClick.fulfilled, (state, action) => {
      console.log("[REDUX THUNK] Recorded ad click:", action.payload);
    });

  }
});

//setters
export const { setParam, setSortBy, storeFilters, setProperties, setCurrentPage, setCaptchaVerified, setCurrentView, clearFilters } = runtimeSlice.actions;
//actions
// export { fetchProperties, fetchLocations , fetchTypes, fetchUsers };
export { fetchProperties, fetchSearchFacets, fetchUsers, getEstimatedHits, fetchAds };
//triggers
// export { updateSearchParams };
export { recordAdView, recordAdClick};
//default export
export default runtimeSlice.reducer;

